/**
 * PlayersGridOverlay — Full-screen sheet that lists every online player as a grid.
 *
 * Opens from the lobby's "SEE ALL". Reuses the same PlayerProfileBadge atom
 * (card variant) the showcase carousel uses, so a player's visual identity is
 * consistent everywhere.
 *
 * Mounting: caller appends `element` somewhere with high stacking context
 * (typically document.body). The component owns its own backdrop fade + sheet
 * slide, sized via env(safe-area-inset-*).
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { PlayerProfileBadge } from '../profile/PlayerProfileBadge';
import type { LobbyPlayer } from '../../../services/LobbyService';

interface PlayersGridOverlayEvents {
  'profile:tap': { playerId: string };
  'close': void;
}

export class PlayersGridOverlay extends EventEmitter<PlayersGridOverlayEvents> {
  public element: HTMLElement;
  private gridEl: HTMLElement;
  private badges: Map<string, PlayerProfileBadge> = new Map();
  private isVisible = false;

  constructor() {
    super();
    PlayersGridOverlay.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-pgrid-root';
    this.element.innerHTML = `
      <div class="sdl-pgrid-backdrop" data-backdrop></div>
      <div class="sdl-pgrid-sheet">
        <header class="sdl-pgrid-header">
          <div class="sdl-pgrid-title-block">
            <div class="sdl-pgrid-crumb">LOBBY</div>
            <div class="sdl-pgrid-title">ON THE BLOCK</div>
          </div>
          <button class="sdl-pgrid-close" type="button" aria-label="Close" data-close>✕</button>
        </header>
        <div class="sdl-pgrid-body" data-grid></div>
      </div>
    `;
    this.gridEl = this.element.querySelector('[data-grid]') as HTMLElement;

    const closeBtn = this.element.querySelector('[data-close]') as HTMLElement;
    // Close only via the X button — tapping a player (opens their card) or the
    // backdrop must not dismiss the grid.
    closeBtn.addEventListener('click', () => this.fireClose());
  }

  show(players: LobbyPlayer[]): void {
    this.setPlayers(players);
    if (this.isVisible) return;
    this.isVisible = true;
    // Force a reflow so the .visible transition runs from the initial state.
    this.element.offsetHeight;
    this.element.classList.add('visible');
  }

  hide(): void {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.element.classList.remove('visible');
  }

  /** Patch the grid in place, reusing badge instances by playerId. */
  setPlayers(players: LobbyPlayer[]): void {
    const seen = new Set<string>();
    const fragment = document.createDocumentFragment();

    players.forEach((player) => {
      seen.add(player.id);
      let badge = this.badges.get(player.id);
      if (badge) {
        badge.update(toBadgeData(player));
      } else {
        badge = new PlayerProfileBadge(toBadgeData(player), 'row');
        badge.on('tap', ({ playerId }) => this.emit('profile:tap', { playerId }));
        this.badges.set(player.id, badge);
      }
      fragment.appendChild(badge.element);
    });

    for (const [id, badge] of this.badges) {
      if (!seen.has(id)) {
        badge.destroy();
        this.badges.delete(id);
      }
    }

    this.gridEl.appendChild(fragment);
  }

  destroy(): void {
    this.badges.forEach((b) => b.destroy());
    this.badges.clear();
    this.element.remove();
    this.removeAllListeners();
  }

  private fireClose(): void {
    this.hide();
    this.emit('close', undefined);
  }

  // ---------- Styles ----------

  private static injectStyles(): void {
    if (document.getElementById('sdl-pgrid-styles')) return;
    const style = document.createElement('style');
    style.id = 'sdl-pgrid-styles';
    style.textContent = `
      .sdl-pgrid-root {
        position: fixed;
        inset: 0;
        z-index: var(--z-modal, 501);
        pointer-events: none;
      }
      .sdl-pgrid-root.visible { pointer-events: auto; }

      .sdl-pgrid-backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        opacity: 0;
        transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .sdl-pgrid-root.visible .sdl-pgrid-backdrop { opacity: 1; }

      .sdl-pgrid-sheet {
        position: absolute;
        left: 0; right: 0; bottom: 0;
        max-width: 480px;
        margin: 0 auto;
        height: 90vh;
        background: var(--dark-bg);
        border-top: 1px solid var(--glass-border);
        border-radius: 20px 20px 0 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        transform: translateY(100%);
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.6);
      }
      .sdl-pgrid-root.visible .sdl-pgrid-sheet { transform: translateY(0); }

      .sdl-pgrid-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: calc(env(safe-area-inset-top) + 14px) 16px 14px;
        border-bottom: 1px solid var(--glass-border);
        flex-shrink: 0;
        background: linear-gradient(180deg, rgba(0,0,0,0.4), transparent);
      }
      .sdl-pgrid-title-block { flex: 1; min-width: 0; }
      .sdl-pgrid-crumb {
        font-family: var(--font-body);
        font-size: 10px;
        letter-spacing: 2.5px;
        color: rgba(255,255,255,0.4);
        font-weight: 600;
      }
      .sdl-pgrid-title {
        font-family: var(--font-display);
        font-size: 22px;
        letter-spacing: 3px;
        line-height: 1;
        background: linear-gradient(180deg, #fff, #c8c8d0);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      .sdl-pgrid-close {
        all: unset;
        width: 36px; height: 36px;
        display: flex; align-items: center; justify-content: center;
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 10px;
        color: white;
        font-size: 14px;
        cursor: pointer;
        transition: transform 0.15s ease, background 0.15s ease;
        flex-shrink: 0;
      }
      .sdl-pgrid-close:active {
        transform: scale(0.92);
        background: var(--concrete-lighter);
      }

      .sdl-pgrid-body {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 14px 16px calc(env(safe-area-inset-bottom) + 16px);
        -webkit-overflow-scrolling: touch;
      }
      .sdl-pgrid-body::-webkit-scrollbar { width: 0; }

      /* Pause continuous animations when the grid isn't visible. The grid
         lives on document.body (outside .sdl-ui), so the in-game display:none
         optimization doesn't reach it. Without this, every avatar's ring
         rotation keeps repainting its conic-gradient during gameplay. */
      .sdl-pgrid-root:not(.visible) .sdl-pbadge-ring {
        animation-play-state: paused;
      }
    `;
    document.head.appendChild(style);
  }
}

function toBadgeData(p: LobbyPlayer) {
  return {
    id: p.id,
    username: p.username,
    ringColor: p.ringColor,
    picture: p.picture,
    // No status dot in the "see all" list — lobby presence implies online.
    division: p.division,
    streak: p.streak,
    streakCold: p.streakCold,
  };
}
